import type { BasicSelectOption } from 'src/components/shared/form/BasicSelect'
import type {
  DocusignPartnerContractTemplate,
  LmsTrainingPlain,
  YardstikPackage,
} from 'src/interfaces/collectStepReference'
import type { InsurancePolicy } from 'src/interfaces/insurancePolicy'
import type { CollectStepReferenceState } from 'src/reducers/collectStepReference/types'
import type { InsurancePolicyState } from 'src/reducers/insurancePolicy/types'
import type { OnboardingFlowEditorState } from 'src/reducers/onboardingFlowEditor/types'

export interface RootState {
  onboardingFlowEditor: OnboardingFlowEditorState
  collectStepReference: CollectStepReferenceState
  insurancePolicy: InsurancePolicyState
}

export const getCollectStepReferenceState = (state: RootState): CollectStepReferenceState =>
  state.collectStepReference

export const getDocusignPartnerTemplates = (state: RootState): DocusignPartnerContractTemplate[] =>
  state.collectStepReference.docusignTemplates

export const getLmsTrainingsPlain = (state: RootState): LmsTrainingPlain[] =>
  state.collectStepReference.lmsTrainings

export const getYardstikPackages = (state: RootState): YardstikPackage[] =>
  state.collectStepReference.yardstikPackages

export const getCollectStepReferenceLoading = (state: RootState): boolean =>
  state.collectStepReference.docusignLoading ||
  state.collectStepReference.lmsLoading ||
  state.collectStepReference.yardstikLoading ||
  state.insurancePolicy.loading

/**
 * DocuSign dropdown: human title + short DocuSign id in label.
 * Value stored in step metadata `docusignTemplateId` is the partner template row `id` (DB id).
 */
export const getDocusignTemplateSelectOptions = (state: RootState): BasicSelectOption[] =>
  state.collectStepReference.docusignTemplates.map((t) => ({
    label: formatDocusignOptionLabel(t),
    value: t.id,
  }))

/** LMS training dropdown: externalId, title, and description in label; value is training `id`. */
export const getLmsTrainingSelectOptions = (state: RootState): BasicSelectOption[] =>
  state.collectStepReference.lmsTrainings.map((t) => ({
    label: formatLmsTrainingOptionLabel(t),
    value: t.id,
  }))

/** Yardstik package dropdown: name as label; value stored in step metadata is `packageId`. */
export const getYardstikPackageSelectOptions = (state: RootState): BasicSelectOption[] =>
  state.collectStepReference.yardstikPackages.map((p) => ({
    label: p.name,
    value: p.packageId,
  }))

/**
 * The strict `InsurancePolicy`, widened with the two aliases the flow editor's helpers have
 * always tolerated. Neither appears in para-node's `InsurancePolicyFullDto`, so in practice
 * they never arrive — but proving that against production data is not this change's job, and
 * `InsurancePolicy` is assignable to this type, so the helpers keep working unmodified.
 */
type FlowEditorInsurancePolicyRow = InsurancePolicy & {
  insurancePolicyId?: string
  title?: string
}

export function resolveInsurancePolicyRowValue(p: FlowEditorInsurancePolicyRow): string {
  const raw = (p.insurancePolicyId ?? p.id ?? '').trim()

  return raw
}

function formatInsurancePolicyOptionLabel(p: FlowEditorInsurancePolicyRow, value: string): string {
  const name = p.name?.trim()
  const title = p.title?.trim()

  if (name) {
    return name
  }

  if (title) {
    return title
  }

  return value
}

/** Insurance policy dropdown: value matches template `insurancePolicyId` (`insurancePolicyId` or `id` on row). */
export const getInsurancePolicySelectOptions = (state: RootState): BasicSelectOption[] => {
  const out: BasicSelectOption[] = []

  for (const policyRow of state.insurancePolicy.policies) {
    const value = resolveInsurancePolicyRowValue(policyRow)

    if (value === '') {
      continue
    }

    out.push({
      label: formatInsurancePolicyOptionLabel(policyRow, value),
      value,
    })
  }

  return out
}

/** Match partner row `id` or DocuSign `docusignTemplateId` (API metadata may store either). */
export function findDocusignPartnerTemplateByStoredValue(
  templates: DocusignPartnerContractTemplate[],
  storedValue: string | null | undefined,
): DocusignPartnerContractTemplate | undefined {
  const normalized = typeof storedValue === 'string' ? storedValue.trim() : ''

  if (normalized === '') {
    return undefined
  }

  return templates.find((t) => {
    const rowId = t.id.trim()
    const docusignId = t.docusignTemplateId.trim()

    return rowId === normalized || docusignId === normalized
  })
}

/**
 * Canonical value for select + API metadata `docusignTemplateId`: partner template row `id`.
 * Accepts legacy values that stored DocuSign's `docusignTemplateId` UUID instead.
 */
export function resolveDocusignTemplateSelectValue(
  templates: DocusignPartnerContractTemplate[],
  storedValue: string | null | undefined,
): string {
  const match = findDocusignPartnerTemplateByStoredValue(templates, storedValue)

  if (match) {
    return match.id.trim()
  }

  return typeof storedValue === 'string' ? storedValue.trim() : ''
}

export function mergeDocusignTemplateSelectOptions(
  options: BasicSelectOption[],
  templates: DocusignPartnerContractTemplate[],
  storedValue: string | null | undefined,
): BasicSelectOption[] {
  const match = findDocusignPartnerTemplateByStoredValue(templates, storedValue)

  if (!match) {
    return mergeSelectOptionForCurrentValue(options, storedValue, 'Deleted Template')
  }

  const canonicalValue = match.id.trim()
  const label = formatDocusignOptionLabel(match)
  const hasCanonical = options.some((o) => String(o.value).trim() === canonicalValue)

  if (hasCanonical) {
    return options
  }

  return [...options, { label, value: canonicalValue }]
}

export function mergeSelectOptionForCurrentValue(
  options: BasicSelectOption[],
  currentValue: string | null | undefined,
  fallbackPrefix: string,
): BasicSelectOption[] {
  let normalized = ''

  if (typeof currentValue === 'string') {
    normalized = currentValue.trim()
  }

  if (normalized === '') {
    return options
  }

  const matches = options.some((o) => {
    const ov = typeof o.value === 'string' ? o.value.trim() : String(o.value)

    return ov === normalized
  })

  if (matches) {
    return options
  }

  return [...options, { label: `${fallbackPrefix}: ${normalized}`, value: normalized }]
}

export function mergeLmsTrainingSelectOptions(
  options: BasicSelectOption[],
  trainings: LmsTrainingPlain[],
  storedValue: string | null | undefined,
): BasicSelectOption[] {
  const normalized = typeof storedValue === 'string' ? storedValue.trim() : ''

  if (normalized === '') {
    return options
  }

  const match = trainings.find((t) => t.id.trim() === normalized)

  if (!match) {
    return mergeSelectOptionForCurrentValue(options, storedValue, 'Training')
  }

  const hasOption = options.some((o) => String(o.value).trim() === normalized)

  if (hasOption) {
    return options
  }

  return [...options, { label: formatLmsTrainingOptionLabel(match), value: match.id }]
}

export function buildLmsTrainingStepLabel(lmsTrainingId: string | null | undefined): string {
  const normalized = typeof lmsTrainingId === 'string' ? lmsTrainingId.trim() : ''

  if (normalized === '') {
    return 'lms_training'
  }

  return `lms_training_${normalized}`
}

function formatLmsTrainingOptionLabel(t: LmsTrainingPlain): string {
  const title = t.title?.trim() || ''
  const description = t.description?.trim() || ''

  if (description !== '') {
    return `${t.externalId} - ${title} (${description})`
  }

  return `${t.externalId} - ${title}`
}

function formatDocusignOptionLabel(t: DocusignPartnerContractTemplate): string {
  const title = t.title?.trim()
  const shortId =
    t.docusignTemplateId.length > 10
      ? `${t.docusignTemplateId.slice(0, 6)}…${t.docusignTemplateId.slice(-4)}`
      : t.docusignTemplateId

  if (title) {
    return `${title} (${shortId})`
  }

  return shortId
}
